/**
 * session-registry — ITEM 98 / §9.
 *
 * Models the two concurrent SIP sessions a real attended transfer requires.
 *
 * WHAT "ATTENDED TRANSFER" DOES TODAY
 * -----------------------------------
 * `useSoftphone.ts:853` labels a block "Attended transfer workflow". The block
 * is `session.hold()` followed by `session.refer(target)` — a BLIND transfer
 * with a hold in front of it.
 *
 * The defining feature of an attended transfer is missing: the agent never
 * speaks to the target first, and cannot abandon the transfer if the target
 * declines. An agent who "attended transfers" a caller to a colleague at lunch
 * has already handed the caller over, irreversibly.
 *
 * WHY IT COULD NOT BE FIXED IN PLACE
 * ----------------------------------
 * `sessionRef` is a single ref, assigned unconditionally at
 * `useSoftphone.ts:367`:
 *
 *     sessionRef.current = session
 *
 * A second session — the consultation leg — REPLACES the first, and the original
 * caller becomes unreachable: no hangup, no unhold, no resume, and the UI no
 * longer knows they exist. There are 19 `sessionRef` usages and every one
 * assumes at most one call.
 *
 * WHY THIS IS A SEPARATE, PURE MODULE
 * -----------------------------------
 * Rewriting those 19 sites is a change to the live media path of every agent.
 * The parts that are actually hard are not the JsSIP calls — they are the
 * DECISIONS at the edges:
 *
 *   - the target declines            -> end consult, resume primary
 *   - the caller hangs up mid-consult -> promote consult, do not orphan it
 *   - a second INVITE arrives         -> must NOT silently replace a live leg
 *   - logout                          -> BOTH legs must be torn down
 *
 * Those are testable here without a SIP stack, and they are where the current
 * implementation is wrong. The hook consumes this; the registry does not call
 * JsSIP itself.
 */

/** The two legs an attended transfer needs. */
export type SessionRole = 'primary' | 'consult'

/** Opaque to this module — JsSIP's RTCSession in production, a stub in tests. */
export type SipSession = unknown

export interface CancelConsultationPlan {
  /** The consultation leg to end. Null when there is none. */
  endSession: SipSession | null
  /** The original call to take off hold. Null when there is none. */
  resumeSession: SipSession | null
}

export interface PrimaryLostPlan {
  /**
   * The consultation leg to promote to primary, when the original caller has
   * gone. Null when there is nothing to promote.
   */
  promote: SipSession | null
}

export interface SessionRegistry {
  get(role: SessionRole): SipSession | null
  /** Throws if the role is already occupied — see the note on `set`. */
  set(role: SessionRole, session: SipSession): void
  clear(role: SessionRole): void
  count(): number
  all(): SipSession[]
  canCompleteTransfer(): boolean
  planCancelConsultation(): CancelConsultationPlan
  planPrimaryLost(): PrimaryLostPlan
}

export function createSessionRegistry(): SessionRegistry {
  const sessions = new Map<SessionRole, SipSession>()

  return {
    get(role) {
      return sessions.get(role) ?? null
    },

    /**
     * THROWS on a double-set, rather than replacing.
     *
     * Silent replacement is precisely the current defect: an incoming INVITE
     * overwrites `sessionRef.current` and the previous caller is orphaned. A
     * throw makes that visible at the point it happens instead of producing a
     * call nobody can hang up.
     *
     * The caller clears a role first when it genuinely wants to reuse it.
     */
    set(role, session) {
      if (sessions.has(role)) {
        throw new Error(
          `Refusing to replace the live ${role} session. Clear it first — ` +
            'silently overwriting orphans the existing call (the defect item 98 fixes).',
        )
      }
      sessions.set(role, session)
    },

    clear(role) {
      sessions.delete(role)
    },

    count() {
      return sessions.size
    },

    /** Both legs, for teardown on logout/unmount. */
    all() {
      return [...sessions.values()]
    },

    /**
     * An attended transfer needs BOTH legs: something to transfer, and someone
     * to transfer it to. With only a primary this is a blind transfer, which is
     * a different verb with different consent implications for the caller.
     */
    canCompleteTransfer() {
      return sessions.has('primary') && sessions.has('consult')
    },

    /**
     * The target declined — end the consultation and return to the caller.
     *
     * This is the whole reason attended transfer exists, and the current
     * implementation has no way back: the REFER has already been sent by the
     * time the agent learns the target is unavailable.
     */
    planCancelConsultation() {
      return {
        endSession: sessions.get('consult') ?? null,
        resumeSession: sessions.get('primary') ?? null,
      }
    },

    /**
     * The original caller hung up while the agent was consulting.
     *
     * The consultation must be PROMOTED rather than orphaned — otherwise the
     * agent is left talking on a session the UI believes does not exist, with no
     * hangup button bound to it.
     */
    planPrimaryLost() {
      return { promote: sessions.get('consult') ?? null }
    },
  }
}
